export const INCOME_OPTIONS = [
  'Moins de 10 000 €',
  '10 000 – 20 000 €',
  '20 000 – 30 000 €',
  '30 000 – 40 000 €',
  '40 000 – 50 000 €',
  '50 000 – 70 000 €',
  '70 000 – 100 000 €',
  '100 000 € et plus',
  'Je ne souhaite pas répondre'
];

export const PERSONAL_CONTEXT_FIELDS = [
  'work_hours_per_week',
  'sleep_hours_per_night',
  'commute_hours_per_week',
  'away_weeks_per_year'
];

export const DURATION_FIELDS = [
  'work_hours_per_week',
  'sleep_hours_per_night',
  'commute_hours_per_week'
];

export const HOUSING_OPTIONS = [
  { value: 'apartment', label: 'Appart' },
  { value: 'house', label: 'Maison' },
  { value: 'house_garden', label: 'Maison + jardin' }
];

const HOUSING_VALUES = HOUSING_OPTIONS.map((o) => o.value);

// Context flags used to dynamically show/hide questionnaire questions.
export const HOUSEHOLD_FLAG_FIELDS = [
  'housing_type',
  'has_pets',
  'has_livestock',
  'has_vehicle',
  'cares_for_dependent'
];

export const HOUSEHOLD_CONTEXT_FIELDS = [
  'household_size',
  'children_full_time_count',
  'children_shared_custody_count',
  'shared_custody_home_percent',
  ...HOUSEHOLD_FLAG_FIELDS
];

export const OPTIONAL_PROFILE_FIELDS = [
  'department',
  'annual_income_bracket',
  'birth_year',
  'gender'
];

export const MANDATORY_FIELDS = [
  ...HOUSEHOLD_CONTEXT_FIELDS,
  ...PERSONAL_CONTEXT_FIELDS
];

function toNullableInt(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function toNullableNumber(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function clampIntOrNull(value, min, max) {
  if (value === null) return null;
  if (!Number.isInteger(value)) return null;
  if (value < min || value > max) return null;
  return value;
}

function clampDecimalOrNull(value, min, max) {
  if (value === null) return null;
  if (!Number.isFinite(value)) return null;
  if (value < min || value > max) return null;
  return Math.round(value * 10000) / 10000;
}

function isMissing(value) {
  return value === '' || value === null || value === undefined;
}

function needsSharedCustodyPercent(form) {
  return Number(form?.children_shared_custody_count || 0) > 0;
}

export function getInitialProfileForm(member) {
  return {
    household_size: member?.household_size ?? '',
    children_full_time_count: member?.children_full_time_count ?? '',
    children_shared_custody_count: member?.children_shared_custody_count ?? '',
    shared_custody_home_percent: member?.shared_custody_home_percent ?? '',
    work_hours_per_week: member?.work_hours_per_week ?? '',
    sleep_hours_per_night: member?.sleep_hours_per_night ?? '',
    commute_hours_per_week: member?.commute_hours_per_week ?? '',
    away_weeks_per_year: member?.away_weeks_per_year ?? '',
    department: member?.department ?? '',
    annual_income_bracket: member?.annual_income_bracket ?? '',
    birth_year: member?.birth_year ?? '',
    gender: member?.gender ?? '',
    housing_type: member?.housing_type ?? '',
    has_pets: member?.has_pets ?? null,
    has_livestock: member?.has_livestock ?? null,
    has_vehicle: member?.has_vehicle ?? null,
    cares_for_dependent: member?.cares_for_dependent ?? null
  };
}

export function getRequiredProfileFields(form, fields = MANDATORY_FIELDS) {
  return fields.filter((field) => {
    if (field === 'shared_custody_home_percent') {
      return needsSharedCustodyPercent(form);
    }
    return true;
  });
}

export function getMissingProfileFields(form, fields = MANDATORY_FIELDS) {
  return getRequiredProfileFields(form, fields).filter((field) => isMissing(form?.[field]));
}

export function isProfileComplete(member) {
  if (!member) return false;
  const form = getInitialProfileForm(member);
  return getMissingProfileFields(form).length === 0;
}

export function buildProfilePayload(form) {
  const sharedCustodyCount = clampIntOrNull(toNullableInt(form.children_shared_custody_count), 0, 10);
  const needsPercent = (sharedCustodyCount || 0) > 0;

  return {
    household_size: clampIntOrNull(toNullableInt(form.household_size), 2, 12),
    children_full_time_count: clampIntOrNull(toNullableInt(form.children_full_time_count), 0, 10),
    children_shared_custody_count: sharedCustodyCount,
    shared_custody_home_percent: needsPercent
      ? clampIntOrNull(toNullableInt(form.shared_custody_home_percent), 0, 100)
      : 0,
    work_hours_per_week: clampDecimalOrNull(toNullableNumber(form.work_hours_per_week), 0, 90),
    sleep_hours_per_night: clampDecimalOrNull(toNullableNumber(form.sleep_hours_per_night), 0, 14),
    commute_hours_per_week: clampDecimalOrNull(toNullableNumber(form.commute_hours_per_week), 0, 30),
    away_weeks_per_year: clampIntOrNull(toNullableInt(form.away_weeks_per_year), 0, 52),
    department: String(form.department || '').trim(),
    annual_income_bracket: form.annual_income_bracket || '',
    birth_year: clampIntOrNull(toNullableInt(form.birth_year), 1900, new Date().getFullYear()),
    gender: form.gender || '',
    housing_type: HOUSING_VALUES.includes(form.housing_type) ? form.housing_type : null,
    has_pets: typeof form.has_pets === 'boolean' ? form.has_pets : null,
    has_livestock: typeof form.has_livestock === 'boolean' ? form.has_livestock : null,
    has_vehicle: typeof form.has_vehicle === 'boolean' ? form.has_vehicle : null,
    cares_for_dependent: typeof form.cares_for_dependent === 'boolean' ? form.cares_for_dependent : null
  };
}

export function findInvalidProfileField(form, payload) {
  return Object.entries(payload).find(([field, value]) => {
    if (OPTIONAL_PROFILE_FIELDS.includes(field)) {
      return false;
    }

    if (field === 'shared_custody_home_percent' && !needsSharedCustodyPercent(form)) {
      return false;
    }

    const raw = form[field];
    return !isMissing(raw) && value === null;
  })?.[0] || null;
}

export function decimalHoursToParts(value) {
  if (value === '' || value === null || value === undefined || !Number.isFinite(Number(value))) {
    return {
      hours: '',
      minutes: ''
    };
  }

  const totalMinutes = Math.round(Number(value) * 60);
  const normalizedHours = Math.floor(totalMinutes / 60);
  const normalizedMinutes = totalMinutes % 60;

  return {
    hours: String(normalizedHours),
    minutes: String(normalizedMinutes).padStart(2, '0')
  };
}

export function partsToDecimalHours({ hours, minutes }) {
  const normalizedHours = hours === '' ? 0 : Number(hours);
  const normalizedMinutes = minutes === '' ? 0 : Number(minutes);

  if (
    (hours === '' || Number.isFinite(normalizedHours)) &&
    (minutes === '' || Number.isFinite(normalizedMinutes))
  ) {
    const bothEmpty = hours === '' && minutes === '';
    if (bothEmpty) return '';

    const clampedHours = Math.max(0, Math.trunc(normalizedHours || 0));
    const clampedMinutes = Math.max(0, Math.min(59, Math.trunc(normalizedMinutes || 0)));
    return Math.round((clampedHours + (clampedMinutes / 60)) * 10000) / 10000;
  }

  return '';
}

export function formatDecimalHoursCompact(value) {
  if (value === '' || value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '-';
  }

  const totalMinutes = Math.round(Number(value) * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h${String(minutes).padStart(2, '0')}`;
}
